import React, { useState, useEffect, useRef, useCallback } from 'react';
import PersonalInfo from './steps/PersonalInfo';
import ContactInfo from './steps/ContactInfo';
import Insurance from './steps/Insurance';
import Medications from './steps/Medications';
import Doctors from './steps/Doctors';
import BenefitsIncome from './steps/BenefitsIncome';
import Review from './steps/Review';
import ScopeOfAppointment from './steps/ScopeOfAppointment';
import SoaAgentSection from './steps/SoaAgentSection';
import ResumeGate from './ResumeGate';

const INTAKE_STEPS = [
  { label: 'Personal Info',  component: PersonalInfo },
  { label: 'Contact Info',   component: ContactInfo },
  { label: 'Benefits',       component: BenefitsIncome },
  { label: 'Insurance',      component: Insurance },
  { label: 'Medications',    component: Medications },
  { label: 'Doctors',        component: Doctors },
];

const SOA_STEP    = { label: 'Scope of Appointment', component: ScopeOfAppointment };
const REVIEW_STEP = { label: 'Review',         component: Review };

function getParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    recordId:      params.get('recordId') || null,
    contactId:     params.get('cid') || null,
    opportunityId: params.get('oid') || null,
    documentId:    params.get('documentId') || null,
    inviteToken:   params.get('t') || null,
    mode:          params.get('mode') || null,
    soa:           params.get('soa') || null,
  };
}

function buildSteps(soa) {
  if (soa === 'only')  return [SOA_STEP];
  if (soa === 'true')  return [SOA_STEP, ...INTAKE_STEPS, REVIEW_STEP];
  if (soa === 'agent') return [{ label: 'Agent Section', component: SoaAgentSection }];
  return [...INTAKE_STEPS, REVIEW_STEP];
}

const initialData = {
  // Personal Info
  firstName: '', middleName: '', lastName: '',
  dob: '', ssn: '', height: '', weight: '', gender: '',
  // Contact Info
  address: '', city: '', county: '', state: '', zip: '',
  mailingIsSameAsPhysical: true,
  mailingAddress: '', mailingCity: '', mailingState: '', mailingZip: '',
  email: '', phone: '',
  // Insurance
  medicareNumber: '', partADate: '', partBDate: '',
  medicaidNumber: '', federalLIS: '',
  isMarried: null,
  isVeteran: null, vaRx: null, hasCurrentPlan: null, hasPrivateInsurance: null, isSmoker: null,
  estimatedEligibility: '',
  pharmacies: [],
  incomeSource: [], otherIncome: '', totalMonthlyIncome: '',
  preferredProviders: [''],
  // Medications
  medications: [],
  // Doctors
  doctors: [],
  // Review
  additionalComments: '',
  // Scope of Appointment — client
  soaProducts: [],
  soaProductTimestamps: {},
  soaIsAuthorizedRep: false,
  soaRepName: '', soaRepRelationship: '',
  soaSignature: '',
  soaAgreedToElectronicSignature: false,
  soaConsentTimestamp: null,
  // Scope of Appointment — agent
  soaContactMethod: '',
  soaAgentName: '', soaAgentPhone: '',
  soaPlansRepresented: '',
  soaAppointmentDate: '',
  soaLateExplanation: '',
  soaAgentSignature: '',
  soaAgentAgreedToElectronicSignature: false,
  soaAgentConsentTimestamp: null,
};

export default function IntakeWizard() {
  const { recordId, contactId, opportunityId, documentId, inviteToken, mode, soa } = getParams();
  const isResumeMode = mode === 'resume' && !!inviteToken;
  const STEPS = buildSteps(soa);
  const storageKey = documentId ? `medicare_intake_soa_${documentId}` : inviteToken ? `medicare_intake_invite_${inviteToken}` : contactId ? `medicare_intake_${contactId}` : recordId ? `medicare_intake_${recordId}` : 'medicare_intake_draft';
  const stateStorageKey = `${storageKey}_state`;

  const [step, setStep] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(stateStorageKey) || '{}');
      return Number.isInteger(saved.step) ? saved.step : 0;
    } catch {
      return 0;
    }
  });
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return initialData;
      const parsed = JSON.parse(saved);
      const normalizeProviders = arr => {
        const trimmed = (arr || []).filter((v, i, a) => v || i === a.length - 1);
        return trimmed.length > 0 ? trimmed : [''];
      };
      const normalizePharmacies = arr =>
        (arr || [])
          .map(p => typeof p === 'string' ? { Pharmacy_Name: p, Pharmacy_NPI: null, Pharmacy_Address: '', Pharmacy_City: '', Pharmacy_State: '' } : p)
          .filter(p => p && (p.Pharmacy_Name || p.name || '').trim());
      return {
        ...initialData,
        ...parsed,
        phone: parsed.phone || parsed.cellPhone || parsed.homePhone || '',
        preferredProviders: normalizeProviders(parsed.preferredProviders),
        pharmacies: normalizePharmacies(parsed.pharmacies),
      };
    } catch {
      return initialData;
    }
  });
  const [submitted, setSubmitted] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [soaSubmitted, setSoaSubmitted] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(stateStorageKey) || '{}');
      return Boolean(saved.soaSubmitted);
    } catch {
      return false;
    }
  });
  const [soaSubmitting, setSoaSubmitting] = useState(false);
  const [agentLoading, setAgentLoading] = useState(soa === 'agent' && !!documentId);
  const [prefillLoading, setPrefillLoading] = useState(!isResumeMode && soa !== 'agent' && !!inviteToken);
  const [resumeAuthorized, setResumeAuthorized] = useState(!isResumeMode);
  const [agentLinkExpired, setAgentLinkExpired] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(120);
  const [expired, setExpired] = useState(false);

  const warnTimerRef    = useRef(null);
  const expireTimerRef  = useRef(null);
  const countdownRef    = useRef(null);

  const TIMEOUT_MS = 15 * 60 * 1000;
  const WARN_MS    =  2 * 60 * 1000;

  const handleExpire = useCallback(() => {
    clearInterval(countdownRef.current);
    if (!soaSubmitted) {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(stateStorageKey);
    }
    setExpired(true);
    setShowTimeoutWarning(false);
  }, [soaSubmitted, storageKey, stateStorageKey]);

  const resetTimers = useCallback(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(expireTimerRef.current);
    clearInterval(countdownRef.current);
    setShowTimeoutWarning(false);
    setSecondsLeft(120);

    warnTimerRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setSecondsLeft(120);
      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) { clearInterval(countdownRef.current); return 0; }
          return s - 1;
        });
      }, 1000);
    }, TIMEOUT_MS - WARN_MS);

    expireTimerRef.current = setTimeout(handleExpire, TIMEOUT_MS);
  }, [handleExpire, TIMEOUT_MS, WARN_MS]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetTimers, { passive: true }));
    resetTimers();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetTimers));
      clearTimeout(warnTimerRef.current);
      clearTimeout(expireTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [resetTimers]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(formData));
    } catch {}
  }, [formData, storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(stateStorageKey, JSON.stringify({ step, soaSubmitted }));
    } catch {}
  }, [stateStorageKey, step, soaSubmitted]);

  useEffect(() => {
    if (soa !== 'agent' || !documentId) return;

    let cancelled = false;
    async function loadSoa() {
      try {
        const res = await fetch(`/api/get-soa?documentId=${encodeURIComponent(documentId)}`);
        const payload = await res.json();
        if (!res.ok) {
          if (res.status === 409) {
            setAgentLinkExpired(true);
            setSubmitError(payload.error || 'This Scope of Appointment has already been completed.');
            return;
          }
          throw new Error(payload.error || `SOA lookup failed (${res.status})`);
        }
        if (cancelled) return;
        setFormData(prev => ({
          ...prev,
          ...(payload.clientData || {}),
          ...(payload.agentPrefill || {}),
          soaAgentPhone: payload.agentPrefill?.soaAgentPhone || prev.soaAgentPhone || '(503) 324-4511',
          contactId: payload.contactId || payload.clientData?.contactId || prev.contactId,
          recordId: payload.contactId || payload.clientData?.recordId || payload.clientData?.contactId || prev.recordId,
          opportunityId: payload.dealId || payload.clientData?.opportunityId || prev.opportunityId,
          dealId: payload.dealId || payload.clientData?.dealId || prev.dealId,
          documentId,
        }));
      } catch (err) {
        if (!cancelled) setSubmitError(err.message);
      } finally {
        if (!cancelled) setAgentLoading(false);
      }
    }

    loadSoa();
    return () => { cancelled = true; };
  }, [soa, documentId]);

  useEffect(() => {
    if (isResumeMode || soa === 'agent' || !inviteToken) return;

    let cancelled = false;
    async function loadPrefill() {
      try {
        const res = await fetch(`/api/prefill?t=${encodeURIComponent(inviteToken)}`);
        if (!res.ok) throw new Error(`Invite prefill failed (${res.status})`);
        const payload = await res.json();
        if (cancelled) return;
        setFormData(prev => ({
          ...prev,
          ...(payload.clientData || {}),
          phone: payload.clientData?.phone || payload.clientData?.cellPhone || payload.clientData?.homePhone || payload.phone || prev.phone || '',
          contactId: payload.contactId || payload.clientData?.contactId || prev.contactId,
          recordId: payload.contactId || payload.clientData?.recordId || payload.clientData?.contactId || prev.recordId,
          opportunityId: payload.dealId || payload.clientData?.opportunityId || prev.opportunityId,
          dealId: payload.dealId || payload.clientData?.dealId || prev.dealId,
          inviteToken,
        }));
      } catch (err) {
        console.warn('Invite prefill failed', err);
      } finally {
        if (!cancelled) setPrefillLoading(false);
      }
    }

    loadPrefill();
    return () => { cancelled = true; };
  }, [isResumeMode, soa, inviteToken]);

  function handleResumeAuthorized(data) {
    setFormData(prev => ({
      ...prev,
      ...(data.formData || {}),
      phone: data.formData?.phone || data.formData?.cellPhone || data.formData?.homePhone || prev.phone || '',
      draftToken: inviteToken,
      formType: data.formType || prev.formType || 'medicare-intake',
      personalCreatorId: data.personalCreatorId || prev.personalCreatorId,
      medicalCreatorId: data.medicalCreatorId || prev.medicalCreatorId,
    }));
    const personalInfoStep = STEPS.findIndex(st => st.label === 'Personal Info');
    setStep(personalInfoStep >= 0 ? personalInfoStep : 0);
    setPrefillLoading(false);
    setResumeAuthorized(true);
  }

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function clientMeta() {
    return {
      userAgent: navigator.userAgent || 'unknown',
      language: navigator.language || '',
      platform: navigator.platform || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    };
  }

  function validateStep(label) {
    if (label === 'Personal Info') {
      if (!String(formData.firstName || '').trim()) return 'First name is required.';
      if (!String(formData.lastName || '').trim()) return 'Last name is required.';
      if (!String(formData.dob || '').trim()) return 'Date of birth is required.';
    }
    if (label === 'Contact Info') {
      if (!String(formData.address || '').trim()) return 'Address is required.';
      if (!String(formData.city || '').trim()) return 'City is required.';
      if (!String(formData.state || '').trim()) return 'State is required.';
      if (!String(formData.zip || '').trim()) return 'ZIP code is required.';
      if (!String(formData.county || '').trim()) return 'County is required.';
      if (!String(formData.email || '').trim()) return 'Email address is required.';
      if (!String(formData.phone || '').trim()) return 'Phone number is required.';
    }
    return '';
  }

  async function handleNext() {
    const validationError = validateStep(STEPS[step].label);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitError(null);
    if (STEPS[step].label === 'Scope of Appointment') {
      setSoaSubmitting(true);
      const crmContactId = formData.contactId || contactId || recordId;
      const crmDealId = formData.opportunityId || opportunityId || formData.dealId;
      const payload = {
        ...formData,
        recordId: formData.recordId || crmContactId,
        contactId: crmContactId,
        cid: crmContactId,
        opportunityId: crmDealId,
        dealId: formData.dealId || crmDealId,
        oid: crmDealId,
        inviteToken: inviteToken || formData.inviteToken,
        soaMode: 'client',
        soaSignatureDate: new Date().toLocaleDateString('en-US'),
        ...clientMeta(),
      };
      try {
        const res = await fetch('/api/submit-soa', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`SOA submission failed (${res.status}). Please try again.`);
        const soaResult = await res.json().catch(() => ({}));
        if (soaResult.documentId || soaResult.agentUrl) {
          setFormData(prev => ({
            ...prev,
            ...(soaResult.clientData || {}),
            documentId: soaResult.documentId || prev.documentId,
            soaDocumentId: soaResult.documentId || prev.soaDocumentId,
            agentUrl: soaResult.agentUrl || prev.agentUrl,
            recordId: prev.recordId || soaResult.clientData?.recordId || soaResult.clientData?.contactId || soaResult.contactId || crmContactId,
            contactId: prev.contactId || soaResult.clientData?.contactId || soaResult.contactId || crmContactId,
            cid: prev.cid || soaResult.clientData?.contactId || soaResult.contactId || crmContactId,
            opportunityId: prev.opportunityId || soaResult.clientData?.opportunityId || soaResult.clientData?.dealId || soaResult.dealId || crmDealId,
            dealId: prev.dealId || soaResult.clientData?.dealId || soaResult.dealId || crmDealId,
            oid: prev.oid || soaResult.clientData?.dealId || soaResult.dealId || crmDealId,
          }));
        }
        setSoaSubmitted(true);
      } catch (err) {
        setSubmitError(err.message);
        setSoaSubmitting(false);
        return;
      }
      setSoaSubmitting(false);
    }
    setStep(st => st + 1);
  }

  function handleBack() {
    let prev = step - 1;
    while (prev >= 0 && soaSubmitted && STEPS[prev].label === 'Scope of Appointment') {
      prev--;
    }
    setStep(Math.max(0, prev));
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);

    const crmContactId = formData.contactId || contactId || recordId;
    const crmDealId = formData.opportunityId || opportunityId || formData.dealId;
    const payload = {
      ...formData,
      recordId: formData.recordId || crmContactId,
      contactId: crmContactId,
      cid: crmContactId,
      opportunityId: crmDealId,
      dealId: formData.dealId || crmDealId,
      oid: crmDealId,
      formType: soa === 'true' ? 'soa-with-intake' : soa === 'only' ? 'soa-only' : 'intake-only',
      documentId: formData.documentId || documentId,
      soaDocumentId: formData.soaDocumentId || formData.documentId || documentId,
      inviteToken: inviteToken || formData.inviteToken,
      soaSignatureDate: new Date().toLocaleDateString('en-US'),
      ...clientMeta(),
    };

    try {
      if (soa !== 'only' && soa !== 'agent') {
        const res = await fetch('/api/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await res.json().catch(() => ({}));
        if (!res.ok || result.ok === false) {
          const detail = result.data?.raw || result.data?.error || result.detail || result.error;
          throw new Error(detail || `Intake submission failed (${res.status}). Please try again.`);
        }
      }

      if ((soa === 'true' && !soaSubmitted) || soa === 'only' || soa === 'agent') {
        const res = await fetch('/api/submit-soa', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...payload,
            soaMode: soa === 'agent' ? 'agent' : 'client',
          }),
        });
        if (!res.ok) throw new Error(`SOA submission failed (${res.status}). Please try again.`);
      }

      localStorage.removeItem(storageKey);
      localStorage.removeItem(stateStorageKey);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveDraft() {
    setSavingDraft(true);
    setSubmitError(null);

    const crmContactId = formData.contactId || contactId || recordId;
    const crmDealId = formData.opportunityId || opportunityId || formData.dealId;
    const payload = {
      ...formData,
      recordId: formData.recordId || crmContactId,
      contactId: crmContactId,
      cid: crmContactId,
      opportunityId: crmDealId,
      dealId: formData.dealId || crmDealId,
      oid: crmDealId,
      currentStep: step,
      formType: formData.formType || (soa === 'true' ? 'soa-with-intake' : 'medicare-intake'),
      inviteToken: inviteToken || formData.inviteToken,
      draftToken: formData.draftToken,
      personalCreatorId: formData.personalCreatorId,
      medicalCreatorId: formData.medicalCreatorId,
      agentEmail: formData.agentEmail,
      agentName: formData.agentName,
      submittedAt: new Date().toISOString(),
      ...clientMeta(),
    };

    try {
      const res = await fetch('/api/save-intake-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(result.error || result.detail || `Save failed (${res.status}).`);
      }
      setFormData(prev => ({
        ...prev,
        draftToken: result.draftToken || prev.draftToken,
        personalCreatorId: result.personalCreatorId || prev.personalCreatorId,
        medicalCreatorId: result.medicalCreatorId || prev.medicalCreatorId,
      }));
      setDraftSaved(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSavingDraft(false);
    }
  }

  const saveAllowedLabels = new Set(['Insurance', 'Medications', 'Doctors', 'Review']);
  const showSaveDraft = soa !== 'only' && soa !== 'agent' && saveAllowedLabels.has(STEPS[step].label);

  if (expired) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏱</div>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Session Expired</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              Your session has expired due to inactivity. For your security, your form data has been cleared.
              Please reload the page to start over.
            </p>
            <button style={{ ...s.btnNext, marginTop: 8 }} onClick={() => window.location.reload()}>
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isResumeMode && !resumeAuthorized) {
    return <ResumeGate token={inviteToken} onAuthorized={handleResumeAuthorized} />;
  }

  if (submitted) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>
              {soa === 'only' ? 'Scope of Appointment Submitted' : 'Thank You!'}
            </h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              {soa === 'agent'
                ? 'The agent section of the Scope of Appointment has been submitted successfully.'
                : soa === 'only'
                ? 'Your Scope of Appointment has been submitted. Your agent will be in touch shortly.'
                : 'Your Medicare intake form has been submitted successfully. Our team will be in touch with you shortly.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (draftSaved) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Saved</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              A secure link has been sent to your email so you can return and finish the form.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (agentLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Loading Scope of Appointment</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              Pulling in the client signature record for the agent section.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (agentLinkExpired) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Scope of Appointment Already Completed</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              This agent signing link has already been used. No additional signature is needed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (prefillLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Loading Form</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              Loading the secure invite information for this form.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const StepComponent = STEPS[step].component;
  const isLast = step === STEPS.length - 1;
  const titleLabel = soa === 'agent' ? 'SCOPE OF APPOINTMENT — AGENT' : soa === 'only' ? 'SCOPE OF APPOINTMENT' : 'MEDICARE CLIENT INTAKE FORM';

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <img src="/logo.png" alt="Translating Insurance" style={s.logoImg} />
      </div>

      {/* Title bar */}
      <div style={s.titleBar}>{titleLabel}</div>

      {/* Progress bar */}
      <div style={s.progressRow}>
        {STEPS.map((st, i) => (
          <React.Fragment key={st.label}>
            <div style={s.progressItem}>
              <button
                type="button"
                onClick={() => !(soaSubmitted && st.label === 'Scope of Appointment') && setStep(i)}
                style={{
                  ...s.dot,
                  background: i < step ? '#1a3364' : i === step ? '#2a5298' : '#b8cfe8',
                  boxShadow: i === step ? '0 0 0 3px #dbeef8, 0 0 0 5px #2a5298' : 'none',
                  border: 'none',
                  cursor: soaSubmitted && st.label === 'Scope of Appointment' ? 'default' : 'pointer',
                  opacity: soaSubmitted && st.label === 'Scope of Appointment' ? 0.6 : 1,
                }}
              >
                {i < step ? '✓' : i + 1}
              </button>
              <span style={{
                ...s.dotLabel,
                color: i === step ? '#1a3364' : i < step ? '#2a5298' : '#8faac5',
                fontWeight: i === step ? 700 : 400,
              }}>
                {st.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ ...s.connector, background: i < step ? '#1a3364' : '#b8cfe8' }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step card */}
      <div style={s.card}>
        <h3 style={s.stepTitle}>{STEPS[step].label}</h3>
        <StepComponent data={formData} onChange={handleChange} />
      </div>

      {/* Navigation */}
      <div style={s.navRow}>
        <div>
          {step > 0 && (
            <button style={s.btnBack} onClick={handleBack}>
              ← Back
            </button>
          )}
        </div>
        {showSaveDraft && (
          <button
            type="button"
            style={{ ...s.btnSaveDraft, opacity: savingDraft ? 0.7 : 1 }}
            onClick={handleSaveDraft}
            disabled={savingDraft}
          >
            {savingDraft ? 'Saving...' : 'Save and Finish Later'}
          </button>
        )}
        <div>
          {!isLast ? (
            <button style={{ ...s.btnNext, opacity: soaSubmitting ? 0.7 : 1 }} onClick={handleNext} disabled={soaSubmitting}>
              {soaSubmitting ? 'Saving…' : 'Next →'}
            </button>
          ) : (
            <button
              style={{ ...s.btnNext, opacity: submitting ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit Form'}
            </button>
          )}
        </div>
      </div>

      {submitError && (
        <div style={{ color: '#c0392b', background: '#fdf0ef', border: '1px solid #f5c6c2', borderRadius: 8, padding: '10px 16px', marginTop: 8, fontSize: 14 }}>
          {submitError}
        </div>
      )}

      {showTimeoutWarning && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3364', color: '#fff', borderRadius: 10,
          padding: '14px 24px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)', fontSize: 14, whiteSpace: 'nowrap',
        }}>
          <span>Your session will expire in <strong>{secondsLeft}s</strong> due to inactivity.</span>
          <button
            onClick={resetTimers}
            style={{
              background: '#fff', color: '#1a3364', border: 'none',
              borderRadius: 6, padding: '6px 16px', fontWeight: 700,
              fontSize: 13, cursor: 'pointer',
            }}
          >
            Still Here
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  container: {
    width: '100%',
    maxWidth: 700,
    margin: '0 auto',
    paddingTop: 32,
  },
  header: {
    textAlign: 'center',
    marginBottom: 12,
  },
  logoImg: {
    maxWidth: 340,
    width: '100%',
    height: 'auto',
  },
  titleBar: {
    background: '#1a3364',
    borderRadius: 8,
    padding: '13px 24px',
    textAlign: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: '0.08em',
    marginBottom: 24,
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 24,
    padding: '0 4px',
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    flex: '0 0 auto',
  },
  dot: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    transition: 'background 0.2s',
  },
  dotLabel: {
    fontSize: 11,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  connector: {
    flex: 1,
    height: 3,
    borderRadius: 2,
    margin: '0 4px',
    marginBottom: 22,
    transition: 'background 0.2s',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '28px 32px',
    boxShadow: '0 2px 16px rgba(26,51,100,0.10)',
    marginBottom: 16,
  },
  stepTitle: {
    color: '#1a3364',
    fontSize: 20,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 24,
    paddingBottom: 14,
    borderBottom: '2px solid #dbeef8',
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  btnBack: {
    padding: '12px 28px',
    background: 'transparent',
    border: '2px solid #1a3364',
    borderRadius: 8,
    color: '#1a3364',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnNext: {
    padding: '12px 32px',
    background: '#1a3364',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSaveDraft: {
    padding: '12px 22px',
    background: '#eef6fb',
    border: '1px solid #b8cfe8',
    borderRadius: 8,
    color: '#1a3364',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
  },
};
